import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import { User, usersApi } from '@/apis/users';
import { clearSession, hasSessionFlag } from '@/utils/authGuard';

const IS_DEV = (import.meta.env.VITE_APP_ENV as string | undefined) === 'development';

export interface Permissions {
  canCapture: boolean;
  canVoid: boolean;
  canRefund: boolean;
  canMarkPaid: boolean;
  canAccessInventory: boolean;
  canAccessAccountIntegration: boolean;
  canAccessUserManagement: boolean;
}

const DEFAULT_PERMISSIONS: Permissions = {
  canCapture: false,
  canVoid: false,
  canRefund: false,
  canMarkPaid: false,
  canAccessInventory: false,
  canAccessAccountIntegration: false,
  canAccessUserManagement: false,
};

function derivePermissions(user: User): Permissions {
  const isSalesperson = user.isSalesperson;
  return {
    canCapture: IS_DEV && !isSalesperson,
    canVoid: IS_DEV && !isSalesperson,
    canRefund: IS_DEV && !isSalesperson,
    // Mirrors the API's @Auth(['admin', 'staff']); assignment scoping is enforced server-side.
    canMarkPaid: user.userRole === 'admin' || user.userRole === 'staff',
    canAccessInventory: !isSalesperson,
    canAccessAccountIntegration: IS_DEV && !isSalesperson,
    canAccessUserManagement: user.userRole === 'admin',
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isDev: boolean;
  permissions: Permissions;
  setUser: (user: User) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isDev: IS_DEV,
      permissions: DEFAULT_PERMISSIONS,

      setUser: (user: User) => {
        set({ user, isAuthenticated: true, permissions: derivePermissions(user) });
      },

      logout: () => {
        set({ user: null, isAuthenticated: false });
        if (typeof window !== 'undefined') {
          clearSession();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      checkAuth: async () => {
        if (typeof window === 'undefined') return;

        // Both tokens live in httpOnly cookies now, invisible to JS. This
        // flag is just a hint that they should still exist, so we skip the
        // network round trip entirely for anonymous visitors. If the access
        // token cookie has expired, axiosClient's response interceptor
        // transparently refreshes it and retries this call.
        if (!hasSessionFlag()) {
          set({ user: null, isAuthenticated: false });
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }

        try {
          const user = await usersApi.getProfile();

          if (user.userRole !== 'admin' && user.userRole !== 'staff') {
            toast.warning('Access denied. You do not have permission to access this portal.');
            get().logout();
            return;
          }

          set({ user, isAuthenticated: true, permissions: derivePermissions(user) });
        } catch (error) {
          console.error('Failed to restore session:', error);
          // Only logout on auth errors — transient failures must not destroy tokens
          const axiosError = error as { response?: { status?: number } };
          const status = axiosError.response?.status;
          if (status === 401 || status === 403) {
            get().logout();
          } else {
            set({ user: null, isAuthenticated: false });
          }
        }
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
      }),
    }
  )
);
